// bridge.ts - the scenario -> view state bridge (S19). Runs @postflop/engine in the browser and maps a
// real engine HandState into the FeltView the <OvalFelt> renders. This is the reusable adaptor S20+
// (Observe / Diagnose / Solo) bind to; S19 exercises it on the locked dx4 teaching hand.
//
// What comes from the ENGINE (live, in-browser): the hero's seat position (assignPositions), whether
// hero holds the button, and the structural HandState the adaptor reads. What is AUTHORED teaching data
// (sanctioned by PRODUCT_NORTH_STAR section 5 for teaching hands): the specific dx4 cards, the per-seat
// ring arrangement, and the pot/bet display. S20 replaces the overlay with the real Observe narration
// builder's played-through state; the adaptor (toFeltView) and the seat ring stay.
import { assignPositions, createHand } from '@postflop/engine';
import type { CardIndex, HandState, Position, ScenarioSpec } from '@postflop/schema';
import { parseCardString } from './cards';

/** One opponent pod placed around the oval. Display arrangement is authored presentation, not derived
 *  from HandState (the engine has no notion of felt geometry). */
export interface SeatView {
  /** TB3: the ENGINE seat this pod is drawing (`HandState.players[].seat`), when it has one. The pod's ring
   *  slot is authored geometry and says nothing about seat numbers, so without this a caller can only GUESS
   *  the mapping (villain index + 1) - and the persistent per-seat read has to be exact or it labels the
   *  wrong man. Optional because the authored teaching ring is a mockup, not a dealt table. */
  seat?: number;
  avatar: string;
  name: string;
  /** display stack, e.g. "$210". */
  stack: string;
  /** inline oval placement, e.g. { left: "9%", top: "60%" }. */
  layout: { left: string; top: string };
  out?: boolean;
  unread?: boolean;
  /** the one seat leading into hero (mint highlight). */
  active?: boolean;
  /** bet chip amount, e.g. "$30"; empty string = no chip this street. */
  bet?: string;
  /** TB3: this seat holds the dealer button. Renders the "D" disc (`.seat .btn-d`, felt.css:147 - authored
   *  in the locked mockup, unrendered until now). Before TB3 only HERO's button drew (`heroHasButton`), so
   *  the button vanished the moment it rotated off hero - at a continuous table it is on a villain 8 hands
   *  out of 9. */
  button?: boolean;
  /** TB3: the read YOU built on this seat, e.g. "Whale" - from `session.seats[i].read.userTags`, so it
   *  persists across hands (TB0 §6.3). This is the compounding-reads payoff made visible: tag seat 4 on
   *  hand 3 and it is still labeled on hand 9, because it is the same guy. Undefined = untagged. */
  tag?: string;
}

/** Everything <OvalFelt> needs for one street. */
export interface FeltView {
  seats: SeatView[];
  board: CardIndex[];
  /** pot display, e.g. "$43". */
  pot: string;
  heroCards: readonly [CardIndex, CardIndex];
  heroLabel: string;
  /** show the dealer button on the hero pod. */
  heroHasButton: boolean;
}

/** How each engine Position reads as plain English in the hero label. */
export const POSITION_LABEL: Record<Position, string> = {
  UTG: 'Under the gun',
  UTG1: 'UTG plus one',
  UTG2: 'UTG plus two',
  LJ: 'Lojack',
  HJ: 'Hijack',
  CO: 'Cutoff',
  BTN: 'Button',
  SB: 'Small blind',
  BB: 'Big blind',
};

/** Whole-dollar display of a chip amount ("$43"). */
export const money = (n: number): string => `$${Math.round(n)}`;

/** The authored dx4 opponent ring. CX2 re-spaces it to the LOCKED CX0 §4.4 even-angular table (Joe's call
 *  at the CX0 gate, 2026-07-16): every seat is an equal step around the oval - the eight villains plus the
 *  hero at bottom sit at ~40 degree intervals on the felt ellipse, four mirror pairs about the vertical axis
 *  (0,7)(1,6)(2,5)(3,4), with the lower pair slid down toward the hero. Points on the ellipse (center 50/50,
 *  rx≈41, ry≈41.5, angle 90+40*i degrees), hand-verified; x kept ≤90% so 440px stays zero-overflow. These
 *  are RING[0..6] (RING_MIRROR is pod 7). The identities (Rock/Vacuum/etc.) are unchanged authored teaching
 *  data; only the slots moved. This re-spacing is app-wide - RING9 feeds every mode. */
export const RING: SeatView[] = [
  { avatar: 'R', name: 'Rock', stack: '$210', layout: { left: '24%', top: '82%' }, out: true },
  { avatar: '?', name: 'unread', stack: '$305', layout: { left: '10%', top: '57%' }, out: true, unread: true },
  { avatar: 'V', name: 'Vacuum', stack: '$142', layout: { left: '15%', top: '29%' }, out: true },
  { avatar: '?', name: 'unread', stack: '$300', layout: { left: '36%', top: '11%' }, out: true, unread: true },
  { avatar: 'F', name: 'Firecracker', stack: '$488', layout: { left: '64%', top: '11%' }, out: true },
  { avatar: '?', name: 'unread', stack: '$260', layout: { left: '85%', top: '29%' }, out: true, unread: true },
  { avatar: 'W', name: 'Whale', stack: '$615', layout: { left: '90%', top: '57%' }, out: true },
];

/**
 * The EIGHTH pod (TB3): the lower-right mirror of `RING[0]`, closing the ring symmetrically - lower-left →
 * up the left → across the top → down the right → back to hero. CX2 moves it to the locked §4.4 lower-right
 * slot (76%, 82%), the mirror of pod 0 (24%, 82%) about the vertical axis. Its identity is untouched; only
 * the slot moved with the rest of the ring.
 */
export const RING_MIRROR: SeatView = {
  avatar: '?', name: 'unread', stack: '$300', layout: { left: '76%', top: '82%' }, out: true, unread: true,
};

/** The full nine-handed ring: eight villain pods + hero's own bottom pod (`FeltView.heroCards`). */
export const RING9: SeatView[] = [...RING, RING_MIRROR];

/**
 * The villain pods for an N-handed table - `RING9` subset for `count` villains, chosen so the oval reads
 * BALANCED at every supported size rather than clustering everyone on the left.
 *
 * This exists because `scenarioToFelt` used to place villains with `RING[i % RING.length]` - a modulo that
 * silently WRAPS, stacking a 9-handed table's eighth villain on top of its first. Selecting instead of
 * wrapping is the fix; the modulo is deleted at the call site.
 *
 * The subsets are AUTHORED, not derived - felt geometry has always been authored presentation in this file
 * (the engine has no notion of an oval). Each was chosen for symmetry about the vertical axis, which is the
 * one thing the eye reads on a table:
 *   - 8 villains (9-handed, the cash default): all eight. Four mirror pairs - (0,7) (1,6) (2,5) (3,4).
 *   - 6 villains (7-handed): drop BOTH lower pods (0 and 7). Three mirror pairs, perfectly symmetric.
 *   - 5 villains (6-handed): both lower pods + the upper-left/upper-right pair + one at the top. Five is
 *     odd, so exactly one pod cannot be mirrored; this puts the unmirrored one nearest top-center, where
 *     the asymmetry is least visible, and keeps players spread over the WHOLE oval the way a short table
 *     actually spreads out.
 */
const RING_SUBSET: Record<number, number[]> = {
  5: [0, 2, 3, 5, 7],
  6: [1, 2, 3, 4, 5, 6],
  8: [0, 1, 2, 3, 4, 5, 6, 7],
};

export const ringFor = (count: number): SeatView[] => {
  const picks = RING_SUBSET[count];
  if (picks) return picks.map((i) => RING9[i]);
  // any other size (2..4 villains, or a size no subset authors): take the balanced middle of the ring, so
  // the pods still sit opposite hero. Never wraps - `count` > RING9.length is the caller's bug, not a
  // silent double-stack.
  if (count > RING9.length) throw new RangeError(`ringFor: ${count} villains exceeds the ${RING9.length}-pod ring`);
  const start = Math.floor((RING9.length - count) / 2);
  return RING9.slice(start, start + count);
};

/** The COLD-TABLE ring (GH4 "New table"): the same authored RING geometry with every seat unread ("?").
 *  ADDED, not a rewrite of RING (the plan is explicit) - this is the presentation for a table you have
 *  not diagnosed yet. `markFeltUnread` applies the same convention to a LIVE felt so the villain
 *  identities read as "?" while the action (who bet, how much) stays visible - a cold read, not a blind one. */
export const RING_UNREAD: SeatView[] = RING.map((s) => ({ ...s, avatar: '?', name: 'unread', unread: true }));

/** Overlay a LIVE felt as an undiagnosed ("New table") read: every villain seat shows "?" instead of its
 *  archetype identity, but its stack, folded state, and - crucially - the live aggressor's bet chip and
 *  highlight are preserved. You see the action from a cold start; you just have not tagged anyone. Hero,
 *  board, and pot are untouched. Mid-session leaves the felt as-is (identities shown = diagnosed). */
export const markFeltUnread = (felt: FeltView): FeltView => ({
  ...felt,
  seats: felt.seats.map((s) => ({ ...s, avatar: '?', name: 'unread', unread: true })),
});

/** The four streets of the locked Observe teaching hand (A-J suited in the Cutoff vs a Vacuum who
 *  leads into hero). Cards, pot, and the villain's bet are authored teaching data. */
interface TeachingStreet {
  board: string[];
  pot: number;
  /** the active Vacuum's bet this street (empty = none). */
  vacuumBet: string;
  vacuumStack: string;
  /** the plain-English hand descriptor after the position ("ace-jack suited"). */
  handDescriptor: string;
}
const TEACHING_STREETS: TeachingStreet[] = [
  { board: [], pot: 7, vacuumBet: '', vacuumStack: '$188', handDescriptor: 'ace-jack suited' },
  { board: ['Jd', '8c', '4s'], pot: 43, vacuumBet: '$30', vacuumStack: '$188', handDescriptor: 'top pair, top kicker' },
  { board: ['Jd', '8c', '4s', '2h'], pot: 103, vacuumBet: '$40', vacuumStack: '$188', handDescriptor: 'top pair, top kicker' },
  { board: ['Jd', '8c', '4s', '2h', '7d'], pot: 183, vacuumBet: '$120', vacuumStack: '$188', handDescriptor: 'top pair, top kicker' },
];

/** Hero's teaching hole cards (A of spades, J of spades). Authored per PRODUCT_NORTH_STAR section 5. */
const HERO_HOLE = ['As', 'Js'] as const;

const HERO_SEAT = 8; // hero sits last on the 9-handed ring so the 8 pods above are the opponents.
const TABLE_SIZE = 9;

/** Find the button seat that puts hero in the Cutoff on a 9-handed table (real engine position math). */
const buttonSeatForHeroCutoff = (): number => {
  for (let b = 0; b < TABLE_SIZE; b++) {
    if (assignPositions(TABLE_SIZE, b).get(HERO_SEAT) === 'CO') return b;
  }
  throw new Error('no button seat places the hero in the Cutoff');
};

/** A minimal $1/$3 spec for the teaching table. Villains fill seats 0..7; hero is seat 8. */
const teachingSpec = (): ScenarioSpec => ({
  gameType: 'cash',
  tableSize: TABLE_SIZE,
  stakes: { sb: 1, bb: 3, currency: 'USD' },
  heroSeat: HERO_SEAT,
  stackDepthBB: 150,
  villains: Array.from({ length: TABLE_SIZE - 1 }, (_, seat) => ({ seat, archetypeId: 'sticker' as const })),
  seed: 'S19-observe-teaching',
});

/**
 * Build the engine HandState for one street of the teaching hand. createHand + assignPositions run in
 * the browser to produce a real state (positions, blinds, pot skeleton); the dx4 cards / pot / street
 * are then overlaid as authored teaching data. Returns the HandState the adaptor consumes.
 */
export const buildTeachingHandState = (streetIdx: number): HandState => {
  const street = TEACHING_STREETS[streetIdx];
  if (!street) throw new RangeError(`teaching street ${streetIdx} out of range (0..3)`);
  const base = createHand({ handId: 'S19-observe', spec: teachingSpec(), buttonSeat: buttonSeatForHeroCutoff() });
  const board = street.board.map(parseCardString);
  const streetName = (['preflop', 'flop', 'turn', 'river'] as const)[streetIdx];
  return {
    ...base,
    street: streetName,
    board,
    pots: [{ amount: street.pot, eligibleSeats: base.pots[0]?.eligibleSeats ?? [] }],
    players: base.players.map((p) =>
      p.seat === HERO_SEAT
        ? { ...p, holeCards: [parseCardString(HERO_HOLE[0]), parseCardString(HERO_HOLE[1])] as [CardIndex, CardIndex] }
        : p,
    ),
  };
};

/**
 * The reusable adaptor: a real engine HandState + the authored street presentation -> the FeltView.
 * Board, pot, hero cards, hero position, and button all come from the HandState; the seat ring is the
 * authored presentation. This is what S20+ reuse (they pass their own played-through HandState).
 */
export const toFeltView = (state: HandState, streetIdx: number): FeltView => {
  const street = TEACHING_STREETS[streetIdx];
  const hero = state.players.find((p) => p.seat === state.spec.heroSeat);
  if (!hero?.holeCards) throw new Error('hero has no hole cards in the teaching state');
  const positionName = POSITION_LABEL[hero.position];
  const activeVacuum: SeatView = {
    avatar: 'V',
    name: 'Vacuum',
    stack: street.vacuumStack,
    // pod 7 (lower-right) of the re-spaced §4.4 ring - the mirror of RING[0], so this 8-pod teaching felt
    // stays an even ring after CX2's app-wide re-spacing.
    layout: { left: '76%', top: '82%' },
    active: true,
    bet: street.vacuumBet,
  };
  return {
    seats: [...RING, activeVacuum],
    board: state.board,
    pot: money(state.pots.reduce((sum, p) => sum + p.amount, 0)),
    heroCards: hero.holeCards,
    heroLabel: `You, ${positionName} . ${street.handDescriptor}`,
    heroHasButton: state.buttonSeat === state.spec.heroSeat,
  };
};

/** Convenience: build the felt view for one street straight from the engine. */
export const teachingFeltView = (streetIdx: number): FeltView =>
  toFeltView(buildTeachingHandState(streetIdx), streetIdx);

// ---------------------------------------------------------------------------------------------------
// Solo felt (S24). Solo has TWO teaching hands - A-J vs the Vacuum and A-Q vs the Firecracker - so the
// felt must render each hand's OWN hole cards on its OWN board with its OWN active opponent. This is the
// DX9 consistency fix: the earlier mockup hardcoded A-J and rendered it on Hand 2's board (a factual lie
// - A-J on Q-9-6 is no pair, not the "top pair, top kicker" the label claimed). Here the cards, board,
// and opponent are per-hand authored data; hero's POSITION/button still come from the engine.

/** One street's felt presentation for a Solo teaching hand (authored, PRODUCT_NORTH_STAR §5): the board,
 *  pot, active-villain bet, and the plain-English hand descriptor shown after the position. */
export interface SoloFeltStreet {
  board: string[];
  pot: number;
  /** the active villain's bet this street ("" = none, e.g. preflop). */
  vbet: string;
  /** the descriptor after "You, Cutoff ." - e.g. "ace-queen suited", "top pair, top kicker". */
  handDescriptor: string;
}

/** The per-hand felt constants for a Solo teaching hand: hero's hole cards + the active opponent seat. */
export interface SoloFeltHand {
  hole: readonly [string, string];
  opp: { avatar: string; name: string; stack: string };
}

/**
 * Build the felt view for one Solo street. Hero's position and button come from the ENGINE (the same
 * teaching table as Observe/Diagnose - hero in the Cutoff); the hole cards, board, pot, and active
 * opponent are the hand's authored data. So Hand 1 (A-J vs the Vacuum on J-8-4) and Hand 2 (A-Q vs the
 * Firecracker on Q-9-6) each render their own cards on their own board - the DX9 fix, by construction.
 */
export const soloFeltView = (hand: SoloFeltHand, street: SoloFeltStreet): FeltView => {
  const base = createHand({ handId: 'S24-solo', spec: teachingSpec(), buttonSeat: buttonSeatForHeroCutoff() });
  const hero = base.players.find((p) => p.seat === base.spec.heroSeat);
  if (!hero) throw new Error('hero seat not found in the solo teaching state');
  const heroCards: readonly [CardIndex, CardIndex] = [
    parseCardString(hand.hole[0]),
    parseCardString(hand.hole[1]),
  ];
  const activeOpp: SeatView = {
    avatar: hand.opp.avatar,
    name: hand.opp.name,
    stack: hand.opp.stack,
    // pod 7 (lower-right) of the re-spaced §4.4 ring - see toFeltView.
    layout: { left: '76%', top: '82%' },
    active: true,
    bet: street.vbet,
  };
  return {
    seats: [...RING, activeOpp],
    board: street.board.map(parseCardString),
    pot: money(street.pot),
    heroCards,
    heroLabel: `You, ${POSITION_LABEL[hero.position]} . ${street.handDescriptor}`,
    heroHasButton: base.buttonSeat === base.spec.heroSeat,
  };
};
